// Puente con la app de Android/iOS (Capacitor). La app carga esta misma web, y
// Capacitor inyecta window.Capacitor; en el navegador nada de esto se ejecuta.
const cap = () => (typeof window !== 'undefined' ? window.Capacitor : undefined);
const VERIFIER_KEY = 'cupido_app_verifier';

export function isNativeApp() {
  return Boolean(cap()?.isNativePlatform?.());
}

const b64url = (bytes) => btoa(String.fromCharCode(...new Uint8Array(bytes)))
  .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

// Google no permite loguearse dentro del WebView: abrimos el login en el navegador
// del sistema y el server nos devuelve un código por cupidoconmate://auth.
export async function startGoogleLoginInApp() {
  const verifier = b64url(crypto.getRandomValues(new Uint8Array(32)));
  const challenge = b64url(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier)));
  try { localStorage.setItem(VERIFIER_KEY, verifier); } catch { /* sin storage no hay login por app */ }
  const url = `${window.location.origin}/api/auth/google?app_challenge=${challenge}`;
  await cap().nativePromise('Browser', 'open', { url, presentationStyle: 'popover' });
}

function handleAppUrl(url) {
  // "Abrir la app" desde la web del celular: ir a la misma pantalla
  if (url && url.startsWith('cupidoconmate://open')) {
    const path = new URL(url.replace('cupidoconmate://', 'https://app/')).searchParams.get('path');
    const target = typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') ? path : '/';
    if (target !== window.location.pathname + window.location.search) window.location.href = target;
    return;
  }
  if (!url || !url.startsWith('cupidoconmate://auth')) return;
  const code = new URL(url.replace('cupidoconmate://', 'https://app/')).searchParams.get('code');
  let verifier = null;
  try { verifier = localStorage.getItem(VERIFIER_KEY); localStorage.removeItem(VERIFIER_KEY); } catch { /* nada */ }
  // Sin verifier es un link ya canjeado (getLaunchUrl lo repite en cada recarga): ignorarlo
  if (!verifier) return;
  cap().nativePromise('Browser', 'close', {}).catch(() => {});
  if (!code) { window.location.href = '/auth?error=oauth'; return; }
  window.location.href = `/api/auth/app-exchange?code=${encodeURIComponent(code)}&verifier=${encodeURIComponent(verifier)}`;
}

export function initNativeApp() {
  if (!isNativeApp()) return;
  cap().addListener('App', 'appUrlOpen', (data) => handleAppUrl(data?.url));
  // Si la app estaba cerrada y la abrió el link de vuelta del login
  cap().nativePromise('App', 'getLaunchUrl', {}).then((r) => handleAppUrl(r?.url)).catch(() => {});
}

// ── Notificaciones push (@capacitor-firebase/messaging) ────────
const PUSH_TOKEN_KEY = 'cupido_push_token';
let pushListenersReady = false;

async function savePushToken(token) {
  if (!token) return;
  const { api } = await import('./api');
  await api.post('/push/token', { token, platform: cap().getPlatform() });
  try { localStorage.setItem(PUSH_TOKEN_KEY, token); } catch { /* nada */ }
}

// Se llama al tener sesión: pide permiso (la primera vez) y registra este celular.
export async function registerPush() {
  // PluginHeaders lo carga la app nativa: si se compiló sin el plugin, no hacer nada
  if (!isNativeApp() || !cap().PluginHeaders?.some((h) => h.name === 'FirebaseMessaging')) return;
  try {
    if (!pushListenersReady) {
      pushListenersReady = true;
      cap().addListener('FirebaseMessaging', 'tokenReceived', (e) => { savePushToken(e?.token).catch(() => {}); });
      // Al tocar una notificación, ir a la pantalla que corresponde (chat, regalos...)
      cap().addListener('FirebaseMessaging', 'notificationActionPerformed', (e) => {
        const path = e?.notification?.data?.path;
        if (typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')) window.location.href = path;
      });
    }
    const perm = await cap().nativePromise('FirebaseMessaging', 'requestPermissions', {});
    if (perm?.receive !== 'granted') return;
    const { token } = await cap().nativePromise('FirebaseMessaging', 'getToken', {});
    await savePushToken(token);
  } catch (e) { console.warn('[push]', e?.message); }
}

// Al cerrar sesión: que este celular deje de recibir avisos de esa cuenta.
export async function unregisterPush() {
  if (!isNativeApp()) return;
  let token = null;
  try { token = localStorage.getItem(PUSH_TOKEN_KEY); localStorage.removeItem(PUSH_TOKEN_KEY); } catch { /* nada */ }
  if (!token) return;
  const { api } = await import('./api');
  await api.post('/push/token/delete', { token }).catch(() => {});
}
